// Canonical create/update facility identity editor, shared by business
// onboarding and Facility Settings.
//
//   - Facility Name + StructuredAddressSection (with a "Use business address"
//     shortcut that copies the org HQ address).
//   - fix/golive-anytime — the Facility Type picker (venue / coach_partner /
//     merchant) is REMOVED from onboarding: the owner doesn't want it in this
//     panel. The backend still requires a valid `facility_type`, so a NEW
//     facility hard-defaults to "venue"; an EXISTING facility keeps whatever
//     type it already has (never silently downgraded by an unrelated save).
//     Changing a facility's type after creation is a `create-facility.tsx`
//     concern (the standalone "add another facility" flow), not onboarding.
//   - On a VERIFIED address the IANA timezone is auto-resolved from
//     geoApi.getTimezone(lat, lng) and flows into the save payload.
//   - Saving calls createFacility (no existing facility) or updateFacility and
//     reports the typed result to its consumer. Selection and cache effects stay
//     with the onboarding/settings surfaces that own them.
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useRef, useState, type FormEvent } from "react";
import type { AddressResult } from "@bw/shared/components";

import { getTimezone } from "@/libs/geoApi";
import {
  createFacility,
  updateFacility,
  type FacilityDetail,
  type FacilityAddressInput,
  type FacilityListItem,
} from "@/libs/facilityApi";
import {
  StructuredAddressSection,
  addressDraftToAddress,
  composeAddressLocation,
  createAddressDraft,
  hasSameAddressDefinition,
  isAddressDraftVerified,
  type AddressDraft,
  type AddressDraftInput,
} from "@/components/address/StructuredAddressSection";

export interface FacilityIdentityEditorProps {
  organizationId: string;
  businessAddress?: AddressDraftInput | null;
  facility: FacilityDetail | null;
  onSaved?: (result: FacilityIdentitySaveResult) => void;
  onPersistenceStateChange?: (state: FacilityIdentityPersistenceState) => void;
}

export interface FacilityIdentityPersistenceState {
  dirty: boolean;
  saving: boolean;
}

export type FacilityIdentitySaveResult =
  | { kind: "created"; facility: FacilityListItem }
  | { kind: "updated"; facility: FacilityDetail };

interface FacilityIdentitySaveTarget {
  scopeVersion: number;
  facilityId: string | null;
  organizationId: string;
  facilityType: FacilityDetail["facility_type"];
  creationIdempotencyKey: string;
}

function normalizeText(value: string | null | undefined): string {
  return (value ?? "").trim();
}

// True when two drafts describe the same address (the base fields + matched
// coordinates). Line 2 is suite/unit metadata and is compared too so an edited
// unit number still counts as a change.
function draftsEqual(a: AddressDraft, b: AddressDraft): boolean {
  return (
    normalizeText(a.line1) === normalizeText(b.line1) &&
    normalizeText(a.line2) === normalizeText(b.line2) &&
    normalizeText(a.city) === normalizeText(b.city) &&
    normalizeText(a.state) === normalizeText(b.state) &&
    normalizeText(a.postalCode) === normalizeText(b.postalCode) &&
    normalizeText(a.country || "US") === normalizeText(b.country || "US") &&
    a.latitude === b.latitude &&
    a.longitude === b.longitude
  );
}

function draftToFacilityAddress(
  draft: AddressDraft,
): FacilityAddressInput | null {
  const address = addressDraftToAddress(draft);
  if (!address) return null;
  return {
    line1: address.line1,
    line2: address.line2,
    city: address.city,
    state: address.state,
    postal_code: address.postal_code,
    country: address.country,
    latitude: address.latitude,
    longitude: address.longitude,
  };
}

export function FacilityIdentityEditor({
  organizationId,
  businessAddress,
  facility,
  onSaved,
  onPersistenceStateChange,
}: FacilityIdentityEditorProps) {
  const [name, setName] = useState(facility?.name ?? "");
  const nameRef = useRef(name);
  const [supportEmail, setSupportEmail] = useState(
    facility?.support_email ?? "",
  );
  const supportEmailRef = useRef(supportEmail);
  const [supportPhone, setSupportPhone] = useState(
    facility?.support_phone ?? "",
  );
  const supportPhoneRef = useRef(supportPhone);
  const [addressDraft, setAddressDraft] = useState(() =>
    createAddressDraft(facility?.address),
  );
  const addressDraftRef = useRef(addressDraft);
  const verificationVersionRef = useRef(0);
  const timezoneDraftVersionRef = useRef<number | null>(facility ? 0 : null);
  const mutationInFlightRef = useRef(false);
  const savedFacilityIdRef = useRef(facility?.facility_id ?? null);
  const savedFacilityOrgIdRef = useRef(organizationId);
  const creationIdempotencyKeyRef = useRef(globalThis.crypto.randomUUID());
  const persistenceStateCallbackRef = useRef(onPersistenceStateChange);
  persistenceStateCallbackRef.current = onPersistenceStateChange;
  const identitySignature = [
    facility?.facility_id,
    facility?.approval_status,
    facility?.name,
    facility?.support_email,
    facility?.support_phone,
    facility?.facility_type,
    facility?.timezone,
    facility?.address?.line1,
    facility?.address?.line2,
    facility?.address?.city,
    facility?.address?.state,
    facility?.address?.postal_code,
    facility?.address?.country,
    facility?.address?.latitude,
    facility?.address?.longitude,
  ].join("\u0000");
  const appliedIdentityRef = useRef(
    `${organizationId}\u0000${identitySignature}`,
  );
  const scopeKey = [
    organizationId,
    facility?.facility_id ?? "new",
    facility?.approval_status ?? "new",
  ].join(":");
  const scopeKeyRef = useRef(scopeKey);
  const scopeVersionRef = useRef(0);
  if (scopeKeyRef.current !== scopeKey) {
    scopeKeyRef.current = scopeKey;
    scopeVersionRef.current += 1;
    mutationInFlightRef.current = false;
  }
  const [timezone, setTimezone] = useState(facility?.timezone ?? "");
  const [timezoneLookupVersion, setTimezoneLookupVersion] = useState<
    number | null
  >(null);
  // No picker sets this anymore (facility-type selection was removed from
  // onboarding) — an existing facility keeps its own type; a new one
  // hard-defaults to "venue" (the backend requires a valid type).
  const facilityType = facility?.facility_type ?? "venue";
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    const nextIdentity = `${organizationId}\u0000${identitySignature}`;
    if (appliedIdentityRef.current === nextIdentity) return;
    appliedIdentityRef.current = nextIdentity;
    const nextDraft = createAddressDraft(facility?.address);
    const nextVersion = verificationVersionRef.current + 1;
    verificationVersionRef.current = nextVersion;
    timezoneDraftVersionRef.current = facility ? nextVersion : null;
    const organizationChanged =
      savedFacilityOrgIdRef.current !== organizationId;
    const enteringFreshCreateScope =
      facility == null && savedFacilityIdRef.current != null;
    if (organizationChanged) {
      savedFacilityOrgIdRef.current = organizationId;
    }
    if (organizationChanged || enteringFreshCreateScope) {
      creationIdempotencyKeyRef.current = globalThis.crypto.randomUUID();
    }
    savedFacilityIdRef.current = facility?.facility_id ?? null;
    const nextName = facility?.name ?? "";
    nameRef.current = nextName;
    setName(nextName);
    const nextSupportEmail = facility?.support_email ?? "";
    supportEmailRef.current = nextSupportEmail;
    setSupportEmail(nextSupportEmail);
    const nextSupportPhone = facility?.support_phone ?? "";
    supportPhoneRef.current = nextSupportPhone;
    setSupportPhone(nextSupportPhone);
    addressDraftRef.current = nextDraft;
    setAddressDraft(nextDraft);
    setTimezone(facility?.timezone ?? "");
    setTimezoneLookupVersion(null);
  }, [facility, identitySignature, organizationId]);

  async function lookupTimezone(
    lat: number,
    lng: number,
  ): Promise<string | null> {
    try {
      const result = await getTimezone(lat, lng);
      return result.timezone;
    } catch {
      return null;
    }
  }

  function captureSaveTarget(): FacilityIdentitySaveTarget {
    return {
      scopeVersion: scopeVersionRef.current,
      facilityId: facility?.facility_id ?? savedFacilityIdRef.current,
      organizationId,
      facilityType,
      creationIdempotencyKey: creationIdempotencyKeyRef.current,
    };
  }

  const mutation = useMutation({
    mutationFn: async ({
      draft,
      timezoneValue,
      nameValue,
      supportEmailValue,
      supportPhoneValue,
      target,
    }: {
      draft: AddressDraft;
      timezoneValue: string;
      nameValue: string;
      supportEmailValue: string;
      supportPhoneValue: string;
      target: FacilityIdentitySaveTarget;
    }): Promise<FacilityIdentitySaveResult> => {
      if (target.scopeVersion !== scopeVersionRef.current) {
        throw new Error("Facility identity changed before this save started.");
      }
      const address = draftToFacilityAddress(draft);
      if (!address) {
        throw new Error("Verify the facility address before saving.");
      }
      if (!timezoneValue) {
        throw new Error("Determine the facility timezone before saving.");
      }
      const supportEmail = supportEmailValue.trim();
      const supportPhone = supportPhoneValue.trim();
      const supportFields = target.facilityId
        ? {
            support_email: supportEmail || null,
            support_phone: supportPhone || null,
          }
        : {
            ...(supportEmail ? { support_email: supportEmail } : {}),
            ...(supportPhone ? { support_phone: supportPhone } : {}),
          };
      const body = {
        name: nameValue,
        location: composeAddressLocation(draft),
        ...supportFields,
        timezone: timezoneValue,
        address,
        facility_type: target.facilityType,
      };
      if (target.facilityId) {
        return {
          kind: "updated",
          facility: await updateFacility(target.facilityId, body),
        };
      }
      const saved = await createFacility({
        idempotency_key: target.creationIdempotencyKey,
        organization_id: target.organizationId,
        ...body,
        facility_type: "venue",
        staff_check_in_available: true,
      });
      return { kind: "created", facility: saved };
    },
    onSuccess: (result, variables) => {
      if (variables.target.scopeVersion !== scopeVersionRef.current) return;
      const saved = result.facility;
      savedFacilityIdRef.current = saved.facility_id;
      setMessage("Saved.");
      setError("");
      onSaved?.(result);
    },
    onError: (err: Error, variables) => {
      if (variables.target.scopeVersion !== scopeVersionRef.current) return;
      setMessage("");
      setError(err.message || "Could not save facility identity.");
    },
    onSettled: (_data, _error, variables) => {
      if (variables.target.scopeVersion !== scopeVersionRef.current) return;
      mutationInFlightRef.current = false;
    },
  });

  function buildVerifiedDraft(result: AddressResult): AddressDraft {
    return createAddressDraft(
      {
        line1: result.address_line1,
        line2: addressDraftRef.current.line2,
        city: result.city,
        state: result.state,
        postal_code: result.postal_code,
        country: result.country,
        latitude: result.lat,
        longitude: result.lng,
      },
      result.formatted_address,
    );
  }

  // Whether the in-editor state differs from the already-saved facility. New
  // facilities are always "dirty" (nothing saved yet). For an existing facility,
  // compare name, address draft, and timezone against the saved record so a
  // bare focus+blur with no edit does NOT fire a redundant PATCH. (facilityType
  // is a derived read-only value now — it can never diverge from the saved
  // record, so it's not part of the dirty-check.)
  function hasUnsavedChanges(
    draft: AddressDraft,
    timezoneValue: string,
  ): boolean {
    if (!facility) return true;
    const savedDraft = createAddressDraft(facility.address);
    return (
      nameRef.current.trim() !== (facility.name ?? "").trim() ||
      supportEmailRef.current.trim() !==
        (facility.support_email ?? "").trim() ||
      supportPhoneRef.current.trim() !==
        (facility.support_phone ?? "").trim() ||
      !draftsEqual(draft, savedDraft) ||
      timezoneValue !== (facility.timezone ?? "")
    );
  }

  const identityDirty = hasUnsavedChanges(addressDraft, timezone);
  const timezoneLookupPending = timezoneLookupVersion !== null;
  useEffect(() => {
    persistenceStateCallbackRef.current?.({
      dirty: identityDirty,
      saving: mutation.isPending || timezoneLookupPending,
    });
  }, [identityDirty, mutation.isPending, timezoneLookupPending]);
  useEffect(
    () => () => {
      persistenceStateCallbackRef.current?.({ dirty: false, saving: false });
    },
    [],
  );

  function autosaveFacility(
    draft = addressDraft,
    timezoneValue = timezone,
    force = false,
    target = captureSaveTarget(),
  ) {
    setMessage("");
    setError("");
    const nameValue = nameRef.current.trim();
    if (target.scopeVersion !== scopeVersionRef.current) return;
    if (!nameValue) return;
    if (!isAddressDraftVerified(draft)) return;
    if (timezoneDraftVersionRef.current !== verificationVersionRef.current) {
      return;
    }
    if (mutationInFlightRef.current || mutation.isPending) return;
    // Dirty-check: skip a no-op PATCH on an unchanged, already-saved facility.
    // A verified-address selection passes force=true (always a real change).
    if (!force && !hasUnsavedChanges(draft, timezoneValue)) return;
    mutationInFlightRef.current = true;
    mutation.mutate({
      draft,
      timezoneValue,
      nameValue,
      supportEmailValue: supportEmailRef.current,
      supportPhoneValue: supportPhoneRef.current,
      target,
    });
  }

  function updateAddressDraft(nextDraft: AddressDraft) {
    if (mutationInFlightRef.current) return;
    const addressDefinitionChanged = !hasSameAddressDefinition(
      addressDraftRef.current,
      nextDraft,
    );
    if (addressDefinitionChanged) {
      verificationVersionRef.current += 1;
      timezoneDraftVersionRef.current = null;
      setTimezoneLookupVersion(null);
    }
    addressDraftRef.current = nextDraft;
    setAddressDraft(nextDraft);
  }

  async function handleAddressVerified(result: AddressResult) {
    if (mutationInFlightRef.current) return;
    const target = captureSaveTarget();
    const nextDraft = buildVerifiedDraft(result);
    const verificationVersion = verificationVersionRef.current + 1;
    verificationVersionRef.current = verificationVersion;
    timezoneDraftVersionRef.current = null;
    setTimezoneLookupVersion(verificationVersion);
    setMessage("");
    setError("");
    addressDraftRef.current = nextDraft;
    setAddressDraft(nextDraft);
    const nextTimezone = await lookupTimezone(result.lat, result.lng);

    const currentDraft = addressDraftRef.current;
    if (
      target.scopeVersion !== scopeVersionRef.current ||
      verificationVersionRef.current !== verificationVersion ||
      !hasSameAddressDefinition(currentDraft, nextDraft) ||
      currentDraft.latitude !== nextDraft.latitude ||
      currentDraft.longitude !== nextDraft.longitude ||
      !isAddressDraftVerified(currentDraft)
    ) {
      setTimezoneLookupVersion((current) =>
        current === verificationVersion ? null : current,
      );
      return;
    }
    setTimezoneLookupVersion((current) =>
      current === verificationVersion ? null : current,
    );
    if (!nextTimezone) {
      setMessage("");
      setError(
        "Could not determine the facility timezone. Verify the address again to retry.",
      );
      return;
    }

    timezoneDraftVersionRef.current = verificationVersion;
    setTimezone(nextTimezone);
    autosaveFacility(currentDraft, nextTimezone, true, target);
  }

  async function handleUseBusinessAddress() {
    if (!businessAddress || mutationInFlightRef.current) return;
    const target = captureSaveTarget();
    const nextDraft = createAddressDraft(businessAddress);
    const verificationVersion = verificationVersionRef.current + 1;
    verificationVersionRef.current = verificationVersion;
    timezoneDraftVersionRef.current = null;
    setTimezoneLookupVersion(verificationVersion);
    setMessage("");
    setError("");
    addressDraftRef.current = nextDraft;
    setAddressDraft(nextDraft);
    if (businessAddress.latitude != null && businessAddress.longitude != null) {
      const nextTimezone = await lookupTimezone(
        businessAddress.latitude,
        businessAddress.longitude,
      );

      const currentDraft = addressDraftRef.current;
      if (
        target.scopeVersion !== scopeVersionRef.current ||
        verificationVersionRef.current !== verificationVersion ||
        !hasSameAddressDefinition(currentDraft, nextDraft) ||
        currentDraft.latitude !== nextDraft.latitude ||
        currentDraft.longitude !== nextDraft.longitude ||
        !isAddressDraftVerified(currentDraft)
      ) {
        setTimezoneLookupVersion((current) =>
          current === verificationVersion ? null : current,
        );
        return;
      }
      setTimezoneLookupVersion((current) =>
        current === verificationVersion ? null : current,
      );
      if (!nextTimezone) {
        setMessage("");
        setError(
          "Could not determine the facility timezone. Use the business address again to retry.",
        );
        return;
      }

      timezoneDraftVersionRef.current = verificationVersion;
      setTimezone(nextTimezone);
      autosaveFacility(currentDraft, nextTimezone, false, target);
    } else {
      setTimezoneLookupVersion(null);
    }
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setMessage("");
    setError("");
    const nameValue = nameRef.current.trim();
    if (!nameValue) {
      setError("Facility name is required.");
      return;
    }
    if (!isAddressDraftVerified(addressDraft)) {
      setError("Verify the facility address before saving.");
      return;
    }
    if (timezoneDraftVersionRef.current !== verificationVersionRef.current) {
      setError("Wait for the verified address timezone before saving.");
      return;
    }
    if (mutationInFlightRef.current) return;
    mutationInFlightRef.current = true;
    mutation.mutate({
      draft: addressDraft,
      timezoneValue: timezone,
      nameValue,
      supportEmailValue: supportEmailRef.current,
      supportPhoneValue: supportPhoneRef.current,
      target: captureSaveTarget(),
    });
  }

  return (
    <Stack component="form" onSubmit={handleSubmit} spacing={2}>
      <TextField
        disabled={mutation.isPending}
        id="facility-identity-name"
        label="Facility Name"
        onBlur={() => autosaveFacility()}
        onChange={(event) => {
          nameRef.current = event.target.value;
          setName(event.target.value);
        }}
        placeholder="Downtown Badminton Center"
        value={name}
      />

      <Stack
        component="section"
        aria-labelledby="facility-support-heading"
        spacing={1}
      >
        <Typography
          component="h2"
          id="facility-support-heading"
          variant="subtitle1"
        >
          Player support
        </Typography>
        <TextField
          disabled={mutation.isPending}
          id="facility-support-email"
          label="Player support email"
          helperText="Optional. Shown to players on this facility page."
          onBlur={() => autosaveFacility()}
          onChange={(event) => {
            supportEmailRef.current = event.target.value;
            setSupportEmail(event.target.value);
          }}
          type="email"
          value={supportEmail}
        />
        <TextField
          disabled={mutation.isPending}
          id="facility-support-phone"
          label="Player support phone"
          helperText="Optional. Shown to players on this facility page."
          onBlur={() => autosaveFacility()}
          onChange={(event) => {
            supportPhoneRef.current = event.target.value;
            setSupportPhone(event.target.value);
          }}
          type="tel"
          value={supportPhone}
        />
      </Stack>

      <StructuredAddressSection
        value={addressDraft}
        onChange={updateAddressDraft}
        idPrefix="facility-identity-address"
        autocompleteLabel="Facility Address"
        autocompletePlaceholder="Search the venue address"
        manualLine1Label="Facility Address Line 1"
        helperText="This is the public venue address players will visit."
        onAddressVerified={handleAddressVerified}
        onUseBusinessAddress={
          businessAddress ? () => void handleUseBusinessAddress() : undefined
        }
        disabled={mutation.isPending}
        embedded
      />

      <Button
        disabled={!identityDirty || mutation.isPending || timezoneLookupPending}
        size="small"
        sx={{ alignSelf: "flex-start", minWidth: 160 }}
        type="submit"
        variant="contained"
      >
        {mutation.isPending
          ? "Saving..."
          : timezoneLookupPending
            ? "Resolving timezone..."
            : "Save identity"}
      </Button>
      {message ? (
        <Alert role="status" severity="success">
          {message}
        </Alert>
      ) : null}
      {error ? (
        <Alert role="alert" severity="error">
          {error}
        </Alert>
      ) : null}
    </Stack>
  );
}
